import React, { useState } from "react";
import axios from "axios";
import { Pencil, Pointer, Undo2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { useEdit } from "../../lib/content";
import { API } from "../../lib/store";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import "./edit.css";

// Floating owner-only toggle to turn edit mode on/off across the site
export function EditToggle() {
  const { isOwner, editMode, setEditMode } = useEdit();
  if (!isOwner) return null;
  return (
    <button
      onClick={() => {
        setEditMode(!editMode);
        document.body.classList.toggle("rj-edit-active", !editMode);
      }}
      data-testid="edit-mode-toggle"
      className={`fixed left-4 bottom-6 z-[70] flex items-center gap-2 rounded-full px-4 py-3 shadow-xl border transition-colors ${
        editMode ? "bg-[#a8842c] text-[#171310] border-[#a8842c]" : "bg-[#171310] text-[#e9d9ac] border-[#a8842c]"
      }`}
      title={editMode ? "Exit edit mode" : "Edit this site"}
    >
      {editMode ? <Pointer size={17} /> : <Pencil size={17} />}
      <span className="text-xs tracking-[0.14em] uppercase font-medium">{editMode ? "Editing — Done" : "Edit Site"}</span>
    </button>
  );
}

function TextEditModal({ open, onClose, id, value, def, multiline, label }) {
  const { save, undo } = useEdit();
  const [val, setVal] = useState(value);
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { setVal(value); }, [value, open]);

  const doSave = async () => {
    setBusy(true);
    try { await save(id, val, multiline ? "html" : "text", value); toast.success("Saved — live now"); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const doUndo = async () => {
    try { const r = await undo(id); setVal(r.deleted ? def : r.value); toast.success("Reverted to previous version"); }
    catch { toast.error("Nothing to undo"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none border-[#d8cdb5] max-w-lg" data-testid="edit-text-modal">
        <DialogHeader><DialogTitle className="font-heading text-xl">{label || "Edit text"}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Edit this content. Changes go live immediately after saving.</DialogDescription>
        {multiline ? (
          <Textarea value={val} onChange={(e) => setVal(e.target.value)} rows={6} className="rounded-none border-[#d8cdb5]" data-testid="edit-text-input" />
        ) : (
          <Input value={val} onChange={(e) => setVal(e.target.value)} className="rounded-none border-[#d8cdb5] h-11" data-testid="edit-text-input" />
        )}
        <div className="flex items-center justify-between gap-2 mt-2">
          <button onClick={doUndo} className="text-xs flex items-center gap-1 text-[#7a7160] hover:text-[#a8842c]" data-testid="edit-undo-btn">
            <Undo2 size={13} /> Undo last
          </button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setVal(def)} className="rounded-none border-[#d8cdb5] text-xs">Reset</Button>
            <Button onClick={doSave} disabled={busy} className="rounded-none bg-[#171310] text-[#e9d9ac] text-xs px-6" data-testid="edit-save-btn">{busy ? "Saving…" : "Save"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ImageEditModal({ open, onClose, id, value, def }) {
  const { save, undo, authHeader } = useEdit();
  const [url, setUrl] = useState(value);
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { setUrl(value); }, [value, open]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/media/upload`, fd, { headers: { ...authHeader() } });
      const abs = `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
      setUrl(abs);
      toast.success("Uploaded & resized");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const doSave = async () => {
    setBusy(true);
    try { await save(id, url, "image", value); toast.success("Image updated — live now"); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none border-[#d8cdb5] max-w-lg" data-testid="edit-image-modal">
        <DialogHeader><DialogTitle className="font-heading text-xl">Replace image</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Upload a new photo or paste an image URL. Changes go live after saving.</DialogDescription>
        {url && <img src={url} alt="" className="w-full max-h-52 object-contain border border-[#eee6d6] bg-white" />}
        <label className="mt-2 flex items-center justify-center gap-2 border border-dashed border-[#c9a24b] py-4 cursor-pointer text-sm text-[#7a6a3f] hover:bg-[#faf7f0]" data-testid="edit-image-upload">
          <Upload size={15} /> {busy ? "Working…" : "Upload a photo (auto-resized)"}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        </label>
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="…or paste an image URL" className="rounded-none border-[#d8cdb5] h-11" data-testid="edit-image-url" />
        <div className="flex items-center justify-between gap-2 mt-2">
          <button onClick={async () => { try { const r = await undo(id); setUrl(r.value); toast.success("Reverted"); } catch { toast.error("Nothing to undo"); } }} className="text-xs flex items-center gap-1 text-[#7a7160] hover:text-[#a8842c]">
            <Undo2 size={13} /> Undo last
          </button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setUrl(def)} className="rounded-none border-[#d8cdb5] text-xs">Reset</Button>
            <Button onClick={doSave} disabled={busy || !url} className="rounded-none bg-[#171310] text-[#e9d9ac] text-xs px-6" data-testid="edit-image-save">Save</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Inline editable TEXT. Renders `default` (or saved override); shows a pencil in edit mode.
export function E({ id, as: Tag = "span", default: def = "", multiline = false, label, className = "", children, ...rest }) {
  const { get, isOwner, editMode } = useEdit();
  const [open, setOpen] = useState(false);
  const value = get(id, def);
  const style = multiline ? { whiteSpace: "pre-line" } : undefined;

  if (!(isOwner && editMode)) {
    return <Tag className={className} style={style} {...rest}>{value}</Tag>;
  }
  return (
    <Tag className={`${className} rj-editable`} style={style} {...rest}>
      {value}
      <span
        className="rj-edit-pencil"
        role="button"
        tabIndex={0}
        data-testid={`edit-pencil-${id}`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
      >
        <Pencil size={13} />
      </span>
      <TextEditModal open={open} onClose={() => setOpen(false)} id={id} value={value} def={def} multiline={multiline} label={label} />
    </Tag>
  );
}

// Inline editable IMAGE.
export function EImg({ id, default: def, alt = "", className = "", wrapperClassName = "", ...rest }) {
  const { get, isOwner, editMode } = useEdit();
  const [open, setOpen] = useState(false);
  const src = get(id, def);
  if (!(isOwner && editMode)) return <img src={src} alt={alt} className={className} {...rest} />;
  return (
    <span className={`rj-editable-img ${wrapperClassName}`}>
      <img src={src} alt={alt} className={className} {...rest} />
      <span className="rj-edit-pencil" role="button" tabIndex={0} data-testid={`edit-img-${id}`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}>
        <Pencil size={13} />
      </span>
      <ImageEditModal open={open} onClose={() => setOpen(false)} id={id} value={src} def={def} />
    </span>
  );
}
